<template>
  <TransitionRoot :show="open" as="template" @after-leave="query = ''" appear>
    <Dialog as="div" class="relative z-10" @close="closeModal()">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-40 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20 md:pt-[100px]">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100" leave="ease-in duration-200" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95">
          <DialogPanel class="mx-auto max-w-xl transform divide-y divide-[#E4E2DF] overflow-hidden rounded-xl bg-[#f9f8f6] shadow-2xl border-2 border-[#e8e5de] transition-all">
            <Combobox>
              <div class="relative">
                <Icon name="iconoir:search" class="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                <ComboboxInput 
                class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm" 
                placeholder="Search..." 
                @change="query = $event.target.value" 
                :value="query"
                autocomplete="off" 
                autocapitalize="off"
                />
              </div>

              <ComboboxOptions v-if="searchQuery && searchResults.length > 0" static class="max-h-96 transform-gpu scroll-py-3 overflow-y-auto p-3">
                <ComboboxOption v-for="item in searchResults" :value="item" as="template" v-slot="{ active }">
                  <li :class="['flex cursor-default select-none rounded-xl p-3 pointer', active && 'bg-gray-100']" @click="goToPage(item.href)">
                    <div :class="['flex h-[80px] w-[80px] flex-none items-baseline justify-center rounded-lg']">
                      <img v-if="item.image" :src="item.image" :alt="item.title ? `${item.title} — search result preview` : ''" class="h-18 w-27">
                    </div>
                    <div class="ml-4 flex-auto">
                      <p :class="['text-sm font-medium', active ? 'text-gray-900' : 'text-gray-700']">
                        {{ item.title }}
                      </p>
                      <p :class="['text-sm', active ? 'text-gray-700' : 'text-gray-500']">
                        {{ item.description }}
                      </p>
                    </div>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>

              <div v-if="searchQuery && searchResults.length < 1" class="px-6 py-14 text-center text-sm sm:px-14">
                <Icon name="iconoir:warning-circle" class="mx-auto h-6 w-6 text-gray-400" />
                <p class="mt-4 font-semibold text-gray-900">No results found</p>
                <p class="mt-2 text-gray-500">No pages found for this search term. Please try again.</p>
              </div>
              
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

const userStore = useUserStore()
const { result, search } = useAlgoliaSearch('prod_ELKER')
const router = useRouter()
const localePath = useLocalePath()

const open = ref(true)
const query = ref('')

const searchResults = ref(null)
const searchQuery = ref(null)

const closeModal = () => {
  open.value = false
  userStore.closeSearch()
}

const track = useTrack()

const goToPage = async href => {

  let url = href
  if (url.includes('localhost:3000')) {
    url = href.split('localhost:3000')[1]
  } else if (url.includes('elker.com')) {
    url = href.split('elker.com')[1]
  }

  // Flush the pending search report before navigating. The modal unmounts on
  // close, which cancels the debounce timer, so without this every fast,
  // successful search was dropped and the search report was biased toward
  // slow and abandoned queries.
  reportSearch(localePath(url).slice(0, 200))

  router.push({ path: localePath(url) })

  setTimeout(() => {
    open.value = false
    userStore.closeSearch()
  }, 100)
}

const filterResults = results => {
  if (!results) return null
  return results.filter(result => {
    const { href } = result
    return !(
      href.includes('/webinars/') ||
      href.includes('/policies/')
    )
  })
}

// On-site search is a modal combobox that never puts the term in the URL, so
// the analytics plugin cannot observe it. Report it here.
//
// Reported on a settle delay rather than per keystroke: the watcher fires on
// every character, and sending each one would record a dozen truncated terms
// per search and swamp the rate limit. `results` is the FILTERED count, which
// is what the user actually saw, not the raw Algolia total.
let searchReportTimer = null
const lastReportedTerm = ref('')

// Emit the pending search once, with the result the visitor chose if there was
// one. Safe to call repeatedly: the term is deduped.
const reportSearch = (chose = null) => {
 try {
  clearTimeout(searchReportTimer)
  const term = (query.value || '').trim()
  if (!term) return
  // A chosen result is always worth recording, even when the debounce already
  // reported this term: deduping before the track call meant `chose` could only
  // ever attach if the click landed inside the 900ms window, i.e. before the
  // reader could have read the results.
  if (term === lastReportedTerm.value && !chose) return
  lastReportedTerm.value = term
  track(chose ? 'search_select' : 'search', {
    term: term.slice(0, 100),
    results: (searchResults.value || []).length,
    ...(chose ? { chose } : {}),
  })
 } catch {
  // Analytics must never block the navigation the visitor asked for: this runs
  // immediately before router.push in goToPage.
 }
}

watch(
  query,
  async () => {
    const searchResult = await search({ query: query.value })
    searchResults.value = filterResults(searchResult.hits)
    searchQuery.value = searchResult.query

    clearTimeout(searchReportTimer)
    if (!(query.value || '').trim()) return
    searchReportTimer = setTimeout(() => reportSearch(), 900)
  },
  { immediate: true }
)

// Flush rather than discard: closing the modal is the end of the search, not a
// reason to forget it.
onBeforeUnmount(() => reportSearch())

</script>